import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ResetParticipant } from '../../../../utilities/DL/PersonalQuickReaction';

/**
 * COMPONENTE: QuickReactionPersonalResetModal
 * [HU-06] Modal de confirmación para "Dar otra oportunidad" (soft-reset individual).
 *
 * Archiva el intento anterior del aficionado y le permite volver a ingresar y jugar
 * sin alterar el resto de la base de datos, registrando un motivo en la auditoría.
 */
export const QuickReactionPersonalResetModal = ({
    isOpen = false,
    onClose = () => {},
    roomId = '',
    participant = null,
    onSuccess = () => {}
}) => {
    const [reason, setReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const presetReasons = [
        'Fallo de conexión o reconexión lenta',
        'Toque involuntario o falso inicio accidental',
        'Problema de sensibilidad en pantalla táctil',
        'Reclamación justificada en módulo de atención',
        'Autorizado por producción y cabina'
    ];

    useEffect(() => {
        if (isOpen) {
            setReason('');
            setErrorMessage('');
            setIsSubmitting(false);
        }
    }, [isOpen, participant]);

    const handleConfirmReset = async (e) => {
        if (e) e.preventDefault();

        if (!participant || !participant.id) {
            setErrorMessage('No se ha seleccionado un participante válido.');
            return;
        }

        if (!reason || !reason.trim()) {
            setErrorMessage('El motivo de la reactivación es obligatorio para fines de auditoría.');
            return;
        }

        setIsSubmitting(true);
        setErrorMessage('');

        try {
            const payload = {
                roomId,
                participantId: participant.id,
                reason: reason.trim(),
                operatorUser: 'admin'
            };

            const response = await ResetParticipant(payload);

            if (response && response.success) {
                if (onSuccess) onSuccess(participant);
                onClose();
            } else {
                setErrorMessage(response?.message || 'Error al conceder otra oportunidad al participante.');
            }
        } catch (error) {
            console.error('[QuickReactionPersonalResetModal] Error al reactivar:', error);
            setErrorMessage('Error de comunicación con el servidor.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen || !participant) return null;

    return (
        <div className="QrpModalOverlay" onClick={onClose}>
            <div
                className="QrpModal QrpModal--medium"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="QrpModal__Header QrpModal__Header--warning">
                    <div className="QrpModal__HeaderTitle">
                        <i className="bi bi-arrow-counterclockwise" />
                        <span>Dar Otra Oportunidad</span>
                    </div>
                    <button
                        type="button"
                        className="QrpModal__CloseBtn"
                        onClick={onClose}
                        title="Cerrar modal"
                    >
                        <i className="bi bi-x-lg" />
                    </button>
                </div>

                {/* Body */}
                <div className="QrpModal__Body">
                    {errorMessage && (
                        <div className="QrpModal__Alert QrpModal__Alert--error">
                            <i className="bi bi-exclamation-octagon-fill" />
                            <span>{errorMessage}</span>
                        </div>
                    )}

                    <p className="QrpModal__Description">
                        Estás a punto de reactivar la participación de este aficionado. Su registro previo
                        quedará archivado con fines de auditoría y se le permitirá jugar nuevamente.
                    </p>

                    {/* Resumen del Participante */}
                    <div className="QrpModal__ParticipantSummary">
                        <div className="QrpModal__SummaryRow">
                            <span className="QrpModal__SummaryLabel">Participante:</span>
                            <span className="QrpModal__SummaryValue font-bold">
                                {participant.participantName || 'Sin Nombre'}
                            </span>
                        </div>
                        <div className="QrpModal__SummaryRow">
                            <span className="QrpModal__SummaryLabel">Correo:</span>
                            <span className="QrpModal__SummaryValue">{participant.email || '—'}</span>
                        </div>
                        <div className="QrpModal__SummaryRow">
                            <span className="QrpModal__SummaryLabel">Tiempo Registrado:</span>
                            <span className="QrpModal__SummaryValue text-warning">
                                {participant.totalTimeMs ? `${participant.totalTimeMs} ms` : (participant.reactionTimeMs ? `${participant.reactionTimeMs} ms` : 'Sin jugada')}
                            </span>
                        </div>
                    </div>

                    {/* Motivo Obligatorio */}
                    <div className="QrpModal__FieldGroup mt-3">
                        <label className="QrpModal__Label">
                            Motivo de la reactivación <span className="text-danger">*</span>
                        </label>

                        {/* Botones de motivos predefinidos rápidos */}
                        <div className="QrpModal__PresetPills">
                            {presetReasons.map((preset, idx) => (
                                <button
                                    key={idx}
                                    type="button"
                                    className={`QrpModal__PresetPill ${reason === preset ? 'QrpModal__PresetPill--active' : ''}`}
                                    onClick={() => setReason(preset)}
                                >
                                    {preset}
                                </button>
                            ))}
                        </div>

                        <textarea
                            className="QrpModal__Textarea mt-2"
                            placeholder="Escribe o selecciona un motivo detallado..."
                            rows={3}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            maxLength={300}
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="QrpModal__Footer">
                    <button
                        type="button"
                        className="QrpModal__Btn QrpModal__Btn--secondary"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="QrpModal__Btn QrpModal__Btn--warning"
                        onClick={handleConfirmReset}
                        disabled={isSubmitting || !reason.trim()}
                    >
                        {isSubmitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                <span>Reactivando...</span>
                            </>
                        ) : (
                            <>
                                <i className="bi bi-arrow-repeat me-2" />
                                <span>Conceder Oportunidad</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

QuickReactionPersonalResetModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    roomId: PropTypes.string,
    participant: PropTypes.object,
    onSuccess: PropTypes.func
};

export default QuickReactionPersonalResetModal;
